import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import type { ClinicalNotificationView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  CriticalStrip,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  acknowledgeNotification,
  myNotificationsQuery,
  notificationReportQuery,
  recordNotificationCommunication,
} from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant, formatTimeOnly } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { collectedToday } from './collect-dialog.tsx';
import { notificationSentence, REPORT_STATUS_LABELS } from './format.ts';
import styles from './lab.module.css';
import { CultureSummary, PreliminaryReport } from './culture-panel.tsx';
import { ResultTable } from './result-table.tsx';

const TITLES: Record<ClinicalNotificationView['kind'], string> = {
  lab_critical_result: 'Critical lab result',
  lab_report_corrected: 'Corrected lab report',
  lab_report_entered_in_error: 'Lab report entered in error',
};

/** A notification is loud when it must be acknowledged: critical, or a report withdrawn. */
function isLoud(n: ClinicalNotificationView): boolean {
  return n.requiresAcknowledgement;
}

/**
 * The strips under the top bar on every signed-in screen (plan §6.3). They poll every 15
 * seconds and on focus. There is no close button: a strip goes away only when its
 * notification is acknowledged. More than two collapse into a count so the screen stays
 * usable, and the loudest come first.
 */
export function NotificationStrips() {
  const notifications = useQuery(myNotificationsQuery);
  const [open, setOpen] = useState<string | null>(null);
  const items = [...(notifications.data?.items ?? [])].sort(
    (a, b) => Number(isLoud(b)) - Number(isLoud(a)),
  );
  if (items.length === 0) return null;
  const shown = items.slice(0, 2);
  const rest = items.length - shown.length;
  const chosen = items.find((n) => n.id === open);
  return (
    <>
      {shown.map((n) => (
        <CriticalStrip
          key={n.id}
          danger={isLoud(n)}
          action={
            <Button
              dense
              variant={isLoud(n) ? 'primary' : 'secondary'}
              onPress={() => {
                setOpen(n.id);
              }}
            >
              {isLoud(n) ? 'Open and acknowledge' : 'Open'}
            </Button>
          }
        >
          {notificationSentence(n)}
          {n.communication === null
            ? ''
            : ` Phoned by the lab at ${formatTimeOnly(n.communication.communicatedAt)}.`}
        </CriticalStrip>
      ))}
      {rest > 0 ? (
        <CriticalStrip
          danger={items.slice(2).some(isLoud)}
          action={
            <Button
              dense
              onPress={() => {
                setOpen(items[2]?.id ?? null);
              }}
            >
              Open the next
            </Button>
          }
        >
          {`${String(rest)} more lab ${rest === 1 ? 'notification' : 'notifications'} to acknowledge.`}
        </CriticalStrip>
      ) : null}
      {chosen === undefined ? null : (
        <NotificationDialog
          notification={chosen}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </>
  );
}

/**
 * The result behind a notification, with the patient banner, and the one way to clear it:
 * "Acknowledge", which records who and when. Escalation holders can also record a phone
 * read-back (plan §6.4); the ordering clinician still acknowledges it themselves.
 */
export function NotificationDialog({
  notification,
  onClose,
}: {
  notification: ClinicalNotificationView;
  onClose: () => void;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const report = useQuery(notificationReportQuery(notification.id));
  const [note, setNote] = useState('');
  const loud = isLoud(notification);

  const done = async (title: string) => {
    toasts.show({ title });
    await queryClient.invalidateQueries({ queryKey: ['me', 'clinical-notifications'] });
    onClose();
  };
  const acknowledge = useMutation({
    mutationFn: () => acknowledgeNotification(notification.id, note.trim() === '' ? null : note),
    onSuccess: () => done('Acknowledged'),
  });

  const canPhone =
    session !== null &&
    can(session, 'lab.critical.escalations') &&
    notification.asEscalation &&
    notification.status === 'open';

  return (
    <Dialog
      title={TITLES[notification.kind]}
      tone={loud ? 'alert' : 'default'}
      isOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <div className={styles.form}>
        {session !== null && can(session, 'patients.read') ? (
          <PatientBannerLoader patientId={notification.patient.id} />
        ) : (
          <p>
            <strong>{notification.patient.displayName}</strong>{' '}
            <span className="wodi-id">{notification.patient.patientNumber}</span>
          </p>
        )}
        {report.isPending ? <LoadingBlock label="Loading the result" rows={3} /> : null}
        {report.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void report.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(report.error, "The result couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {report.isSuccess ? (
          <>
            <p className={styles.meta}>
              {report.data.testName}, sample{' '}
              <span className="wodi-id">{report.data.sampleNumber}</span>, collected{' '}
              {formatInstant(report.data.collectedAt)}. {REPORT_STATUS_LABELS[report.data.status]}.
            </p>
            {notification.kind === 'lab_report_entered_in_error' ? (
              <Notice tone="info">
                {`This report was withdrawn: ${report.data.statusReason ?? 'no reason given'}. Don't act on its results.`}
              </Notice>
            ) : null}
            {report.data.correctionReason === null ? null : (
              <p>
                <strong>Corrected: </strong>
                {report.data.correctionReason}
              </p>
            )}
            {report.data.results.length === 0 ? null : <ResultTable report={report.data} />}
            {report.data.culture === null ? null : (
              <CultureSummary culture={report.data.culture} label={report.data.testName} />
            )}
            {(() => {
              const latest = report.data.preliminaryReports.at(-1);
              return report.data.culture === null && latest !== undefined ? (
                <PreliminaryReport report={latest} testName={report.data.testName} />
              ) : null;
            })()}
            {report.data.comment === null ? null : <p>{report.data.comment}</p>}
          </>
        ) : null}
        {notification.communication === null ? null : (
          <p>
            {`Phoned by ${notification.communication.by.displayName} to ${notification.communication.clinicianName} at ${formatTimeOnly(notification.communication.communicatedAt)}, read back.`}
          </p>
        )}
        <TextField label="Note (optional)" value={note} onChange={setNote} />
        {acknowledge.isError ? (
          <Notice tone="error">
            {errorText(acknowledge.error, "The acknowledgement wasn't saved. Try again.")}
          </Notice>
        ) : null}
        {canPhone ? <RecordPhoneCall notificationId={notification.id} onDone={done} /> : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Later
        </Button>
        <Button
          variant="primary"
          onPress={() => {
            acknowledge.mutate();
          }}
          isPending={acknowledge.isPending}
          pendingLabel="Acknowledging…"
          isDisabled={!online || !report.isSuccess}
        >
          Acknowledge
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RecordPhoneCall({
  notificationId,
  onDone,
}: {
  notificationId: string;
  onDone: (title: string) => Promise<void>;
}) {
  const online = useOnline();
  const [clinician, setClinician] = useState('');
  const [time, setTime] = useState('');
  const [readBack, setReadBack] = useState(false);
  const [tried, setTried] = useState(false);
  const [openedAt] = useState(() => new Date());
  const at = time.trim() === '' ? openedAt : collectedToday(time, openedAt);
  const record = useMutation({
    mutationFn: () =>
      recordNotificationCommunication(notificationId, {
        clinicianName: clinician.trim(),
        communicatedAt: (at ?? openedAt).toISOString(),
        readBack: true,
      }),
    onSuccess: () => onDone('Phone call recorded'),
  });
  const go = () => {
    setTried(true);
    if (clinician.trim().length < 3 || at === null || !readBack) return;
    record.mutate();
  };
  return (
    <section className={styles.section} aria-labelledby="phone-call">
      <h3 id="phone-call" className={styles.sectionHeading}>
        Phone the clinician
      </h3>
      <div className={styles.row}>
        <TextField
          label="Clinician phoned"
          value={clinician}
          onChange={setClinician}
          {...(tried && clinician.trim().length < 3
            ? { errorMessage: "Enter the clinician's name." }
            : {})}
        />
        <TextField
          label="Time phoned"
          description="Leave empty for now."
          value={time}
          onChange={setTime}
          {...(at === null ? { errorMessage: 'Enter a time today, like 14:05.' } : {})}
        />
      </div>
      <Checkbox
        isSelected={readBack}
        onChange={setReadBack}
        {...(tried && !readBack
          ? { errorMessage: 'Only a read-back closes the call for the lab.' }
          : {})}
      >
        The clinician read the result back
      </Checkbox>
      {record.isError ? (
        <Notice tone="error">
          {errorText(record.error, "The phone call wasn't recorded. Try again.")}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          onPress={go}
          isPending={record.isPending}
          pendingLabel="Recording…"
          isDisabled={!online}
        >
          Record phone call
        </Button>
      </div>
    </section>
  );
}
